import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  FormControl,
  FormLabel,
  Select,
  Input,
  Button,
  Grid,
  GridItem,
  FormErrorMessage,
  Alert,
  AlertIcon,
  AlertTitle,
} from "@chakra-ui/react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { fetchAttendanceReport } from "../redux/slices/reportsSlice";
import { downloadAttendanceReport } from "../services/downloadService";
import { setloadTost } from "../redux/slices/toastSlice";
import { attendanceReportSchema } from "../utils/formValidationSchema";
import DownloadBTN from "./DownloadBTN";
import SearchAndFilter from "./SearchAndFilter";
import CustomTable from "./CustomTable";
import DotLoader from "./loaders/DotLoader";

const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const AttendanceReport = () => {
  const dispatch = useDispatch();
  const allEvents = useSelector(
    (state) => state.eventRegistration?.allEvents || []
  );
  const membershipTypes = useSelector(
    (state) => state.membershipType?.membershipType || []
  );
  const { attendanceReports, attendanceLoading, attendanceError } = useSelector(
    (state) => state.reports || {}
  );

  const [downloading, setDownloading] = useState(false);
  const [lastPayload, setLastPayload] = useState(null);
  const [selectedEventName, setSelectedEventName] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [requestError, setRequestError] = useState("");

  const initialValues = {
    eventId: "",
    startDate: "",
    endDate: "",
    membershipType: "",
    attendanceType: "",
  };

  const handleGenerate = async (values) => {
    setRequestError("");
    setHasSearched(true);

    const payload = {
      eventId: Number(values.eventId),
      startDate: new Date(values.startDate).toISOString(),
      endDate: new Date(values.endDate).toISOString(),
      membershipType: values.membershipType || "",
      attendanceType: values.attendanceType || "",
    };

    setLastPayload(payload);

    const ev = allEvents.find(
      (e) => String(e.id) === String(values.eventId)
    );
    setSelectedEventName(ev?.name || "");

    try {
      const resultAction = await dispatch(fetchAttendanceReport(payload));
      if (fetchAttendanceReport.rejected.match(resultAction)) {
        const errorMsg =
          resultAction.payload?.responseDescription ||
          resultAction.payload?.message ||
          (typeof resultAction.payload === "string"
            ? resultAction.payload
            : "Failed to generate attendance report. Please try again.");
        setRequestError(errorMsg);
      }
    } catch (err) {
      setRequestError(err?.message || "An unexpected error occurred.");
    }
  };

  const handleDownload = async () => {
    if (!lastPayload) {
      dispatch(
        setloadTost({
          title: "No report selected",
          description: "Please generate an attendance report before downloading.",
          status: "warning",
        })
      );
      return;
    }

    try {
      setDownloading(true);
      const { blob, contentType, fileName } =
        await downloadAttendanceReport(lastPayload);

      const fileBlob = new Blob([blob], { type: contentType });
      const url = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      dispatch(
        setloadTost({
          title: "Download successful",
          description: "Attendance report downloaded successfully.",
          status: "success",
        })
      );
    } catch (err) {
      dispatch(
        setloadTost({
          title: "Download failed",
          description:
            err?.message ||
            "Something went wrong while downloading the attendance report.",
          status: "error",
        })
      );
    } finally {
      setDownloading(false);
    }
  };

  // Resolve attendance data array from possible API responses
  const reportData = Array.isArray(attendanceReports)
    ? attendanceReports
    : Array.isArray(attendanceReports?.data)
    ? attendanceReports.data
    : Array.isArray(attendanceReports?.result)
    ? attendanceReports.result
    : [];

  const canDownload = Boolean(lastPayload);

  const tableProps = {
    data: reportData,
    title: {
      fullName: "Full Name",
      email: "Email",
      attendanceType: "Attendance Type",
      membershipType: "Membership Type",
      reference: "Reference ID",
      date: "Date",
    },
    dataKeys: [
      "fullName",
      "email",
      "attendanceType",
      "membershipType",
      "reference",
      "date",
    ],
    variant: true,
    sn: true,
    transformRow: (item) => {
      const name =
        item?.fullName ||
        (item?.firstName
          ? `${item.firstName} ${item?.lastName || ""}`.trim()
          : item?.name || "-");
      const dateVal =
        item?.dateCreated ||
        item?.createdDate ||
        item?.attendanceDate ||
        item?.date;
      const formattedDate = dateVal
        ? new Date(dateVal).toLocaleDateString()
        : "-";

      return {
        fullName: <Box cursor={"pointer"}>{name}</Box>,
        email: <Box cursor={"pointer"}>{item?.email || "-"}</Box>,
        attendanceType: (
          <Box cursor={"pointer"}>{item?.attendanceType || "-"}</Box>
        ),
        membershipType: (
          <Box cursor={"pointer"}>
            {item?.membershipType || item?.membershipTypeName || "-"}
          </Box>
        ),
        reference: (
          <Box cursor={"pointer"}>
            {item?.reference || item?.referenceNo || item?.refCode || "-"}
          </Box>
        ),
        date: <Box cursor={"pointer"}>{formattedDate}</Box>,
      };
    },
  };

  return (
    <Box>
      <Text color={"brand.primary"} fontSize={"16px"} mb={2} fontWeight={"500"}>
        Select options below to generate attendance report
      </Text>

      {(requestError || attendanceError) && (
        <Alert status="error" mb={4} borderRadius={"4px"}>
          <AlertIcon />
          <AlertTitle fontSize={"14px"}>
            {requestError ||
              (typeof attendanceError === "string"
                ? attendanceError
                : attendanceError?.responseDescription ||
                  attendanceError?.message ||
                  "Failed to load attendance report.")}
          </AlertTitle>
        </Alert>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={attendanceReportSchema}
        onSubmit={handleGenerate}
      >
        {({
          errors,
          touched,
          values,
          handleBlur,
          handleChange,
          setFieldValue,
        }) => (
          <Form>
            <Grid
              templateColumns={[
                "repeat(1, 1fr)",
                "repeat(2, 1fr)",
                "repeat(3, 1fr)",
                "repeat(5, 1fr)",
              ]}
              gap={4}
              py={4}
            >
              {/* Event Dropdown */}
              <GridItem>
                <FormControl isInvalid={errors.eventId && touched.eventId}>
                  <FormLabel htmlFor="eventId" fontSize={"14px"} fontWeight={"500"}>
                    Event Name *
                  </FormLabel>
                  <Select
                    name="eventId"
                    placeholder="Select event"
                    bg={"#FAFAFA"}
                    border={"0.5px solid #8A8C8E"}
                    _placeholder={{ color: "#8A8C8E" }}
                    focusBorderColor="0.5px solid #8A8C8E"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      handleChange(e);
                      const ev = allEvents.find(
                        (item) => String(item.id) === String(selectedId)
                      );
                      if (ev) {
                        if (ev.startDate) {
                          setFieldValue(
                            "startDate",
                            formatDateForInput(ev.startDate)
                          );
                        }
                        if (ev.endDate) {
                          setFieldValue(
                            "endDate",
                            formatDateForInput(ev.endDate)
                          );
                        }
                      }
                    }}
                    value={values.eventId}
                  >
                    {allEvents && allEvents.length > 0 ? (
                      allEvents.map((event) => (
                        <option key={event?.id} value={event?.id}>
                          {event?.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No events available
                      </option>
                    )}
                  </Select>
                  {touched.eventId && errors.eventId && (
                    <FormErrorMessage fontSize={"12px"}>
                      {errors.eventId}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </GridItem>

              {/* Start Date */}
              <GridItem>
                <FormControl isInvalid={errors.startDate && touched.startDate}>
                  <FormLabel htmlFor="startDate" fontSize={"14px"} fontWeight={"500"}>
                    Start Date *
                  </FormLabel>
                  <Input
                    type="date"
                    name="startDate"
                    bg={"#FAFAFA"}
                    border={"0.5px solid #8A8C8E"}
                    focusBorderColor="0.5px solid #8A8C8E"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.startDate}
                  />
                  {touched.startDate && errors.startDate && (
                    <FormErrorMessage fontSize={"12px"}>
                      {errors.startDate}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </GridItem>

              {/* End Date */}
              <GridItem>
                <FormControl isInvalid={errors.endDate && touched.endDate}>
                  <FormLabel htmlFor="endDate" fontSize={"14px"} fontWeight={"500"}>
                    End Date *
                  </FormLabel>
                  <Input
                    type="date"
                    name="endDate"
                    bg={"#FAFAFA"}
                    border={"0.5px solid #8A8C8E"}
                    focusBorderColor="0.5px solid #8A8C8E"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.endDate}
                  />
                  {touched.endDate && errors.endDate && (
                    <FormErrorMessage fontSize={"12px"}>
                      {errors.endDate}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </GridItem>

              {/* Membership Type (Optional) */}
              <GridItem>
                <FormControl>
                  <FormLabel
                    htmlFor="membershipType"
                    fontSize={"14px"}
                    fontWeight={"500"}
                  >
                    Membership Type
                  </FormLabel>
                  <Select
                    name="membershipType"
                    placeholder="All Membership Types"
                    bg={"#FAFAFA"}
                    border={"0.5px solid #8A8C8E"}
                    _placeholder={{ color: "#8A8C8E" }}
                    focusBorderColor="0.5px solid #8A8C8E"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.membershipType}
                  >
                    {membershipTypes &&
                      membershipTypes.map((type, idx) => (
                        <option
                          key={type?.id || idx}
                          value={type?.name || type?.membershipTypeName}
                        >
                          {type?.name || type?.membershipTypeName}
                        </option>
                      ))}
                  </Select>
                </FormControl>
              </GridItem>

              {/* Attendance Type (Optional) */}
              <GridItem>
                <FormControl>
                  <FormLabel
                    htmlFor="attendanceType"
                    fontSize={"14px"}
                    fontWeight={"500"}
                  >
                    Attendance Type
                  </FormLabel>
                  <Select
                    name="attendanceType"
                    placeholder="All Attendance Types"
                    bg={"#FAFAFA"}
                    border={"0.5px solid #8A8C8E"}
                    _placeholder={{ color: "#8A8C8E" }}
                    focusBorderColor="0.5px solid #8A8C8E"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.attendanceType}
                  >
                    <option value="physical">Physical</option>
                    <option value="virtual">Virtual</option>
                    <option value="Author">Author</option>
                  </Select>
                </FormControl>
              </GridItem>
            </Grid>

            {/* Generate Button */}
            <Flex justifyContent={"flex-start"} mt={2} mb={6}>
              <Button
                type="submit"
                bg={"brand.primary"}
                color={"#FFFFFF"}
                _hover={{ bg: "#1e8446" }}
                borderRadius={"4px"}
                px={8}
                py={2}
                isLoading={attendanceLoading}
                loadingText="Generating..."
              >
                Generate Report
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>

      {/* Action / Download Bar */}
      <Flex
        flexDirection={["column", "column", "row"]}
        gap={4}
        justifyContent={"space-between"}
        alignItems={["flex-start", "flex-start", "center"]}
        mt={4}
        mb={4}
      >
        <Box>
          {selectedEventName && (
            <Text fontSize={"16px"} fontWeight={"600"} color={"#333333"}>
              {selectedEventName}
              {reportData.length > 0 && (
                <Text
                  as={"span"}
                  fontSize={"14px"}
                  fontWeight={"400"}
                  color={"#666666"}
                  ml={2}
                >
                  ({reportData.length}{" "}
                  {reportData.length === 1 ? "attendee" : "attendees"})
                </Text>
              )}
            </Text>
          )}
        </Box>

        <DownloadBTN
          btn_Text="Download Report"
          bg={"#61CE70"}
          onClick={handleDownload}
          isDisabled={!canDownload}
          isLoading={downloading}
        />
      </Flex>

      {/* Results Section */}
      {attendanceLoading ? (
        <Box w={"100%"} textAlign={"center"} py={"40px"}>
          <DotLoader />
        </Box>
      ) : hasSearched ? (
        reportData.length > 0 ? (
          <>
            <Flex justifyContent={"flex-end"} alignItems={"center"} my={4}>
              <SearchAndFilter />
            </Flex>
            <CustomTable {...tableProps} />
          </>
        ) : (
          <Box
            py={"60px"}
            textAlign={"center"}
            border={"1px dashed #B4B1B1"}
            borderRadius={"8px"}
            bg={"#FAFAFA"}
            mt={4}
          >
            <Text color={"#666666"} fontSize={"16px"}>
              No attendance records found for the selected filter criteria.
            </Text>
          </Box>
        )
      ) : (
        <Box
          py={"60px"}
          textAlign={"center"}
          border={"1px dashed #E2E8F0"}
          borderRadius={"8px"}
          bg={"#FAFAFA"}
          mt={4}
        >
          <Text color={"#8A8C8E"} fontSize={"16px"}>
            Please select an event, date range, and click &quot;Generate Report&quot; to view attendance data.
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default AttendanceReport;
